import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PersonIcon from '@mui/icons-material/Person';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import {
  MenuItem,
  Stack,
  IconButton,
  Popover,
  Box,
  Typography,
  Divider,
  Button,
  Avatar,
} from '@mui/material';

//------------------------------------------------------------------------------------
import { HttpErrorHandler } from '../../../utils/ErrorHandler';
import { usePopup } from '../../../context/PopupContext';
import { ResetPasswordDialog } from '../../../utils/ResetPasswordDialog';
import GlobalData from '../../../globalData';
import API from '../../../api';

/**
 * @returns Admin Profile Switch drop down menu UI
 */
export default function ProfileSwitch() {
  // General
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [roles, setRoles] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToSelect, setUserToSelect] = useState(null);
  const [open, setOpen] = useState(null);
  const [arrowIconRotated, setArrowIconRotated] = useState(false);

  // popup handler
  const { showPopup, setPopUpText } = usePopup();

  // Open Menu
  const handleOpen = (event) => {
    if (roles.length > 1) {
      setOpen(event.currentTarget);
      setArrowIconRotated(true);
    }
  };

  // Close Menu
  const handleClose = () => {
    setOpen(null);
    setArrowIconRotated(false);
  };

  // Change User handler
  const handleUserChange = (role) => {
    // Api Update Role
    API.patch(`/users/${userInfo.id}/change_profile`, { role_id: userToSelect.prof_type_id })
      .then((response) => {
        if (response.status === 200) {
          // Update Role Status
          const updatedRoles = roles.map((r) => ({
            ...r,
            last_used: r === role ? 1 : 0,
          }));

          setRoles(updatedRoles);

          // Update User Data
          const updatedUserData = {
            ...userInfo,
            roles: updatedRoles,
          };

          // Update Local Storage userData Roles Info
          localStorage.setItem('userData', JSON.stringify(updatedUserData));

          // Get Unselected Role
          const unselectedRole = roles.find((r) => r !== role);

          // Update Selected User
          setSelectedUser(role);

          // Update Unselected User
          setUserToSelect(unselectedRole);

          // Close Menu
          handleClose();

          // Redirect
          navigate(role.prof_type_name === 'USER' ? '/user/dashboard' : '/admin/dashboard', { replace: true });
        }
      })
      .catch((error) => {
        // Close Menu
        handleClose();

        // error handling
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  useEffect(() => {
    const storedUserInfo = JSON.parse(localStorage.getItem('userData'));

    if (!storedUserInfo) {
      navigate('/');
    } else {
      setUserInfo(storedUserInfo);
      setRoles(storedUserInfo.roles);

      const initialSelectedUser = storedUserInfo.roles.find((role) => role.last_used === 1);
      const initialUnselectedUser = storedUserInfo.roles.find((role) => role.last_used === 0);
      setSelectedUser(initialSelectedUser);
      setUserToSelect(initialUnselectedUser);

      const hasAdminRole = storedUserInfo.roles.some((role) => role.prof_type_name === 'ADMIN');

      if (!hasAdminRole) {
        navigate('/');
      }
    }
  }, [navigate]);

  /* -------------------------------- Settings -------------------------------- */
  const [openSettings, setOpenSettings] = useState(null);

  const handleOpenSettings = (event) => {
    setOpenSettings(event.currentTarget);
  };

  const handleCloseSettings = () => {
    setOpenSettings(null);
  };

  // Reset Password
  const [openResetPassword, setOpenResetPassword] = useState(false);

  // Open reset password model handler
  const handleOpenResetPassword = () => {
    setOpenResetPassword(true);
  };

  // Close reset password model handler
  const handleCloseResetPassword = () => {
    setOpenResetPassword(false);
  };

  // Logout
  const onUserLogout = () => {
    API.post('/logout')
      .then((response) => {
        GlobalData.token = null;
        GlobalData.userData = null;
        GlobalData.loginData = null;
        localStorage.clear();
        sessionStorage.clear();
        navigate('/logout');
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  return (
    <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
      {/* drop down menu */}
      <IconButton
        onClick={handleOpen}
        sx={{
          width: '130px',
          height: '30px',
          padding: 0,
          borderRadius: '12px',
        }}
      >
        <Stack
          sx={{
            color: 'white',
            backgroundColor: '#e10714',
            width: '100%',
            height: '100%',
            fontSize: '.6em',
            borderRadius: '12px',
            boxShadow: '4px 4px 8px lightgray',
            padding: '4px 12px',
            textTransform: 'capitalize',
          }}
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          {selectedUser && selectedUser.prof_type_name.toLowerCase()}
          {roles.length > 1 ? (
            <KeyboardArrowUpIcon className={arrowIconRotated ? 'rotate180' : 'rotate0'} sx={{ marginTop: '2px' }} />
          ) : (
            ''
          )}
        </Stack>
      </IconButton>

      {/* role menu */}
      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ marginTop: '2px' }}
      >
        <Stack spacing={0.75}>
          {roles.map((role) => (
            <MenuItem
              key={role.prof_type_id}
              sx={{
                marginTop: '0 !important',
                textTransform: 'capitalize',
                width: '130px',
                display: 'flex',
                justifyContent: 'start',
                alignItems: 'center',
                gap: '8px',
              }}
              selected={role.value === selectedUser.value}
              disabled={!!role.last_used}
              onClick={() => handleUserChange(role)}
            >
              {role.prof_type_name.toLowerCase() === 'user' ? (
                <PersonIcon sx={{ color: '#3c3c3c' }} />
              ) : (
                <ManageAccountsIcon sx={{ color: '#3c3c3c' }} />
              )}
              {role.prof_type_name.toLowerCase()}
            </MenuItem>
          ))}
        </Stack>
      </Popover>

      <IconButton sx={{ height: '42px', width: '42px' }} onClick={handleOpenSettings}>
        <Avatar sx={{ height: '32px', width: '32px' }}>
          <PersonIcon />
        </Avatar>
      </IconButton>

      <Popover
        open={!!openSettings}
        anchorEl={openSettings}
        onClose={handleCloseSettings}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ my: 1.5, px: 2 }}>
          <Box>
            {userInfo &&
              (userInfo.name_alias === null ? (
                <Box>
                  <Typography variant="subtitle2">{userInfo.name}</Typography>
                  <Typography variant="subtitle2">{userInfo.surname}</Typography>
                </Box>
              ) : (
                <Typography variant="subtitle2">{userInfo.name_alias}</Typography>
              ))}
          </Box>

          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {userInfo && userInfo.fiscal_code}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ my: 1.5, px: 2 }}>
          <Button color="secondary" onClick={handleOpenResetPassword}>
            Modifica Password
          </Button>

          <ResetPasswordDialog openDialog={openResetPassword} closeDialog={handleCloseResetPassword} />
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ my: 1.5, px: 2 }}>
          <Button color="error" onClick={onUserLogout}>
            Logout
          </Button>
        </Box>
      </Popover>

    </Box>
  );
}
